import { DatePicker, Modal, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { CloseCircleIcon } from '../../../Icons';
import { LOCAL_EN } from '@/config';
import { useCallback, useState } from 'react';
import { Heading } from '../../../Heading';
import dayjs, { Dayjs } from 'dayjs';
import { groupStatusesByMerchantTitle } from '@/utils/helpers/orderStatusHelpers';
import { SelectiveTag } from '@/components';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (
    selectedDateRange?: string[],
    selectedMonthRange?: string[],
    selectedStatuses?: string[],
    selectedCarriers?: string[],
  ) => void;
}

const exportStatusList = groupStatusesByMerchantTitle();

// Static carrier list - matches FiltersModal
const carrierList: string[] = [
  'Sandoog Express',
  'FastShip Logistics',
  'QuickDelivery Co.',
  'SpeedPost',
  'GlobalCarrier',
];

export const ExportModal = ({ isOpen, onClose, onExport }: ExportModalProps) => {
  const [selectedDateRange, setSelectedDateRange] = useState<[Dayjs, Dayjs] | undefined>(undefined);
  const [selectedMonthRange, setSelectedMonthRange] = useState<[Dayjs, Dayjs] | undefined>(
    undefined,
  );
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedCarriers, setSelectedCarriers] = useState<string[]>([]);

  const { t, i18n } = useTranslation();

  const formatDate = (dates?: [Dayjs, Dayjs]) => {
    if (dates)
      return [
        dates?.[0].startOf('day').format('YYYY-MM-DD HH:mm'),
        dates?.[1].endOf('day').format('YYYY-MM-DD HH:mm'),
      ];

    return undefined;
  };

  const handleExport = () => {
    const formattedDateRange = formatDate(selectedDateRange);
    const formattedMonthRange = formatDate(selectedMonthRange);

    const selectedOrderStatus = selectedStatuses.flatMap((item) => exportStatusList[item]);

    onExport(formattedDateRange, formattedMonthRange, selectedOrderStatus, selectedCarriers);
    handleClose();
  };

  const resetModal = () => {
    setSelectedDateRange(undefined);
    setSelectedMonthRange(undefined);
    setSelectedStatuses([]);
    setSelectedCarriers([]);
  };

  const handleClose = () => {
    onClose();
    resetModal();
  };

  const handleChange = (tag: string, checked: boolean) => {
    const nextSelectedTags = checked
      ? [...selectedStatuses, tag]
      : selectedStatuses.filter((t) => t !== tag);
    setSelectedStatuses(nextSelectedTags);
  };

  const handleCarrierChange = (tag: string, checked: boolean) => {
    const nextSelectedTags = checked
      ? [...selectedCarriers, tag]
      : selectedCarriers.filter((t) => t !== tag);
    setSelectedCarriers(nextSelectedTags);
  };

  const renderStatusTags = useCallback(() => {
    return Object.entries(exportStatusList).map<React.ReactNode>((tag, index) => {
      const [groupName, subGroupList] = tag;

      return (
        <SelectiveTag
          key={subGroupList[0] + index}
          selectedTags={selectedStatuses}
          tagKey={groupName}
          tagLabel={t(`merchant_order_status.${subGroupList[0]}`)}
          handleChange={handleChange}
        />
      );
    });
  }, [selectedStatuses]);

  const renderCarrierTags = useCallback(() => {
    return carrierList.map<React.ReactNode>((carrier) => {
      return (
        <SelectiveTag
          key={carrier}
          selectedTags={selectedCarriers}
          tagKey={carrier}
          tagLabel={carrier}
          handleChange={handleCarrierChange}
        />
      );
    });
  }, [selectedCarriers]);

  return (
    <Modal
      className='rounded-[20px] bg-white border border-customGrayShade3'
      title={
        <div
          className='font-bold text-lg leading-5 uppercase text-[rgb(57,57,60)]'
          style={{ borderBottom: '1px solid #E1E1E3', paddingBottom: '20px' }}
        >
          {t('export')}
        </div>
      }
      width={780}
      centered
      open={isOpen}
      footer={
        <div className='flex gap-4 justify-end'>
          <button
            type='button'
            onClick={resetModal}
            className='h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
          >
            <Heading
              upperCase
              leadingIcon={<CloseCircleIcon width={16} height={16} />}
              title={t('clear_all')}
              fontWeight='font-bold'
              fontSize='text-sm'
            />
          </button>
          <button
            type='button'
            onClick={handleExport}
            className='h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(230,217,60)] transition-colors'
          >
            <Heading upperCase title={t('export')} fontWeight='font-bold' fontSize='text-sm' />
          </button>
        </div>
      }
      onClose={handleClose}
      onCancel={handleClose}
    >
      <div className='flex flex-col gap-4 mt-6'>
        <Typography.Text className='font-normal text-base leading-[20.48px] text-[rgb(57,57,60)]'>
          {t('by_status')}
        </Typography.Text>
        <div className='flex gap-4 flex-wrap'>
          {renderStatusTags()}
        </div>
      </div>
      {/* <div className='flex flex-col gap-4 mt-6'>
        <Typography.Text className='font-normal text-base leading-[20.48px] text-[rgb(57,57,60)]'>
          {t('by_carrier')}
        </Typography.Text>
        <div className='flex gap-4 flex-wrap'>
          {renderCarrierTags()}
        </div>
      </div> */}
      <div className='flex flex-col gap-4 w-1/2 mt-10 mb-10'>
        <Typography.Text className='font-normal text-base leading-[20.48px] text-[rgb(57,57,60)]'>
          {t('order_created_in')}
        </Typography.Text>
        {/* ${i18n.language === LOCAL_EN ? 'pl-10' : 'pr-10'}  */}
        <DatePicker.RangePicker
          className={`w-full custom-date-picker  border border-solid border-customGrayShade5 rounded-lg h-[46px]
             text-center font-bold`} // Tailwind for styling the DatePicker
          format='YYYY-MM-DD'
          placeholder={[t('start_date'), t('end_date')]}
          showNow
          onChange={(dates) => {
            if (dates) setSelectedDateRange(dates as [Dayjs, Dayjs] | undefined);
          }}
          maxDate={dayjs()}
          value={selectedDateRange}
        />
        {!selectedDateRange && (
          <div className='flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgb(255,251,219)] border border-solid border-[rgb(237,224,74)]/30'>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='rgb(180,160,20)' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><circle cx='12' cy='12' r='10'/><line x1='12' y1='8' x2='12' y2='12'/><line x1='12' y1='16' x2='12.01' y2='16'/></svg>
            <span className='text-[12px] text-[rgb(120,100,10)]'>
              {String(t('export_default_period'))}
            </span>
          </div>
        )}
        {/* <DatePicker.RangePicker
          picker='month'
          className={`w-full custom-date-picker  border border-solid border-customGrayShade5 rounded-lg h-[46px]  ${i18n.language === LOCAL_EN ? 'pl-10' : 'pr-10'} text-center font-bold`} // Tailwind for styling the DatePicker
          placeholder={[t('start_month'), t('end_month')]}
          showNow
          onChange={(dates) => {
            if (dates) setSelectedMonthRange(dates as [Dayjs, Dayjs] | undefined);
          }}
          value={selectedMonthRange}
        /> */}
      </div>
    </Modal>
  );
};
